import { useBillingInvoices, useBillingSubscription, useCreateCheckoutSession } from '../lib/hooks';
import {
  Button,
  EmptyState,
  Icon,
  PageHeader,
  SkeletonList,
  StatusBadge,
  useToast,
} from '../components/ui';

export function BillingPage() {
  const subscription = useBillingSubscription();
  const invoices = useBillingInvoices();
  const checkout = useCreateCheckoutSession();
  const { toast } = useToast();

  const currentBase = window.location.origin;
  const invoiceRows = (invoices.data || []) as any[];

  return (
    <>
      <PageHeader
        title="Billing"
        description="Your subscription, plan limits, and invoice history. Payments are handled securely by Stripe."
      />

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)' }}>
        <section className="card">
          <h3>Subscription</h3>

          {subscription.isLoading ? (
            <SkeletonList rows={2} height={64} />
          ) : subscription.data ? (
            <>
              <div className="inline" style={{ margin: '10px 0 14px' }}>
                <StatusBadge status={subscription.data.status} />
                <span className="muted">
                  Plan: <strong style={{ color: 'var(--ink)' }}>{subscription.data.planCode}</strong>
                </span>
              </div>
              <div className="grid kpis">
                <div className="kpi">
                  <div className="label">Locations included</div>
                  <div className="value">{subscription.data.maxLocations}</div>
                </div>
                <div className="kpi">
                  <div className="label">Team seats included</div>
                  <div className="value">{subscription.data.maxSeats}</div>
                </div>
              </div>
              <p className="muted" style={{ marginTop: 14 }}>
                Need more locations or seats? Contact support from the help button and we will adjust your plan.
              </p>
            </>
          ) : (
            <EmptyState
              icon={<Icon.Card size={20} />}
              title="No active subscription"
              description="Activate a subscription to enable automated posting for this workspace."
              action={
                <Button
                  onClick={async () => {
                    try {
                      const session = await checkout.mutateAsync({
                        priceId: 'price_sandbox_agency_growth',
                        successUrl: `${currentBase}/billing?checkout=success`,
                        cancelUrl: `${currentBase}/billing?checkout=cancel`,
                      });
                      if (session.url) {
                        window.location.href = session.url;
                      }
                    } catch (error) {
                      toast({
                        variant: 'error',
                        title: 'Could not open checkout',
                        description: (error as Error).message,
                      });
                    }
                  }}
                  loading={checkout.isPending}
                  loadingText="Opening secure checkout…"
                >
                  Start subscription
                </Button>
              }
            />
          )}
        </section>

        <section className="card">
          <h3>Invoices</h3>
          <p className="card-sub">Every invoice is also emailed to your billing contact.</p>
          {invoices.isLoading ? (
            <SkeletonList rows={3} height={40} />
          ) : invoiceRows.length === 0 ? (
            <EmptyState
              icon={<Icon.Card size={20} />}
              title="No invoices yet"
              description="Your invoices will appear here after your first billing cycle."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceRows.map((invoice: any) => (
                    <tr key={invoice.id}>
                      <td className="mono">
                        {invoice.issuedAt ? new Date(invoice.issuedAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <StatusBadge status={invoice.status} />
                      </td>
                      <td>
                        {invoice.currency?.toUpperCase()} {invoice.amount}
                      </td>
                      <td>
                        {invoice.hostedUrl ? (
                          <a href={invoice.hostedUrl} target="_blank" rel="noreferrer">
                            Open <Icon.External size={12} />
                          </a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
